import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const whatsappUrl = `https://wa.me/14377570478?text=${encodeURIComponent(
    'Hi VK Tax Guide! I would like to know more about your tax filing services.'
  )}`;

  return (
    <footer className="bg-[#005782] text-white py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Brand Column */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_accounting-sms-hub/artifacts/7650y7gp_WhatsApp%20Image%202026-01-28%20at%2011.28.02.jpeg"
                alt="VK Tax Guide"
                className="h-16 w-auto bg-white rounded-lg p-2"
              />
            </div>
            <p className="text-xl text-white font-medium mb-3">
              Guiding Canadians through complex taxes with total clarity!
            </p>
            <div className="inline-block bg-white text-[#005782] px-6 py-3 rounded-lg font-bold text-lg shadow-md mb-6">
              Right filings. Zero stress.
            </div>

            {/* Social Media Icons */}
            <div className="mt-6">
              <p className="text-sm text-white/70 mb-3 uppercase tracking-wider font-semibold">
                Follow Us
              </p>
              <div className="flex items-center gap-3">
                {/* Instagram */}
                <a
                  href="https://www.instagram.com/vktaxguide/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  aria-label="Follow us on Instagram"
                  className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg"
                  style={{
                    background:
                      'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                  }}
                >
                  <svg
                    className="w-6 h-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.058-1.28.073-1.689.073-4.948 0-3.259-.015-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </a>

                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/in/varshil-khatri/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-linkedin-link"
                  aria-label="Connect on LinkedIn"
                  className="w-12 h-12 rounded-xl bg-[#0077B5] flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg"
                >
                  <svg
                    className="w-6 h-6 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>

                {/* WhatsApp */}
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  aria-label="Message us on WhatsApp"
                  className="w-12 h-12 rounded-xl bg-[#25D366] flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg"
                >
                  <svg
                    className="w-6 h-6 text-white"
                    viewBox="0 0 32 32"
                    fill="currentColor"
                  >
                    <path d="M16 0C7.164 0 0 7.164 0 16c0 2.825.738 5.488 2.031 7.794L0 32l8.394-2.031C10.7 31.263 13.362 32 16 32c8.836 0 16-7.164 16-16S24.836 0 16 0zm0 29.5c-2.538 0-4.95-.7-7-1.913l-.506-.3-5.231 1.263 1.263-5.231-.3-.506C3.2 20.95 2.5 18.538 2.5 16 2.5 8.544 8.544 2.5 16 2.5S29.5 8.544 29.5 16 23.456 29.5 16 29.5z" />
                    <path d="M22.744 18.756c-.394-.2-2.331-1.156-2.694-1.288-.362-.131-.625-.2-.887.2-.263.4-1.019 1.288-1.25 1.55-.231.263-.462.3-.856.1-.394-.2-1.663-.612-3.169-1.956-1.169-1.044-1.956-2.331-2.187-2.725-.231-.394-.025-.606.175-.8.181-.175.394-.462.594-.694.2-.231.263-.394.394-.656.131-.263.069-.494-.031-.694-.1-.2-.887-2.138-1.219-2.925-.325-.769-.656-.669-.887-.681-.231-.012-.494-.012-.756-.012s-.694.1-1.056.494c-.362.394-1.381 1.35-1.381 3.294s1.412 3.819 1.612 4.081c.2.263 2.825 4.313 6.844 6.05.956.413 1.7.656 2.281.838.956.306 1.831.263 2.519.163.769-.113 2.331-.956 2.656-1.881.325-.925.325-1.719.231-1.881-.094-.163-.356-.263-.75-.463z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-xl mb-6">Quick Links</h4>
            <ul className="space-y-3 text-white/90">
              <li>
                <Link to="/" className="hover:text-white transition-colors hover:underline">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-white transition-colors hover:underline">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-white transition-colors hover:underline">
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to="/tax-resources"
                  className="hover:text-white transition-colors hover:underline"
                >
                  Tax Resources
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="hover:text-white transition-colors hover:underline"
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Get in Touch */}
          <div>
            <h4 className="font-semibold text-xl mb-6">Get in Touch</h4>
            <div className="space-y-4 text-white/90">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-base leading-relaxed">20 Bay St. 11th floor</p>
                  <p className="text-base">Toronto, ON M5J 2N8</p>
                  <p className="text-base">Canada</p>
                </div>
              </div>
              <a
                href="tel:+14377570478"
                data-testid="footer-phone-link"
                className="flex items-center gap-3 hover:text-white transition-colors group"
              >
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <span className="text-lg">+1 (437) 757-0478</span>
              </a>
              <a
                href="mailto:info@vktax.ca"
                data-testid="footer-email-link"
                className="flex items-center gap-3 hover:text-white transition-colors group"
              >
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <span className="text-lg">info@vktax.ca</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center text-white/70">
          <p>&copy; {new Date().getFullYear()} VK Tax Guide. All rights reserved.</p>
          <div className="flex gap-6 mt-4 md:mt-0">
            <Link to="/" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/" className="hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
